import React, { useState, useEffect } from 'react';
import { CityState } from '../simulation/types';
import { Users, DollarSign, Smile, Calendar, Clock, Play, Pause, ChevronRight, ChevronsRight, Award } from 'lucide-react';

interface HUDProps {
  state: CityState;
  onSetSpeed: (speed: number) => void;
  onTogglePanel: (panel: 'economy' | 'none') => void;
  activePanel: 'economy' | 'none';
}

export const HUD: React.FC<HUDProps> = ({ state, onSetSpeed, onTogglePanel, activePanel }) => {
  const [formattedTime, setFormattedTime] = useState<string>('12:00');

  useEffect(() => {
    const timer = setInterval(() => {
      if ((window as any).swisslandFormattedTime) {
        setFormattedTime((window as any).swisslandFormattedTime);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const getMonthName = (m: number): string => {
    const names = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return names[m - 1] || 'Mese';
  };

  const xpPercent = Math.min(100, Math.round((state.xp / state.xpNeeded) * 100));

  return (
    <div className="hud-bar glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', pointerEvents: 'auto' }}>
      {/* Upper stats row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '16px' }}>
        
        {/* Core metrics panel */}
        <div className="hud-section" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {/* Populazione */}
          <div className="hud-item" title="Abitanti totali" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={18} style={{ color: '#00bcd4' }} />
            <span style={{ fontWeight: 800, fontSize: '14px' }}>{state.population.toLocaleString()}</span>
          </div>

          {/* Tesoreria e Cashflow */}
          <div className="hud-item" title="Casse Comunali" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <DollarSign size={18} style={{ color: state.treasury < 0 ? '#ff1744' : '#4caf50' }} />
            <span style={{ fontWeight: 800, fontSize: '14px', color: state.treasury < 0 ? '#ff1744' : 'inherit' }}>
              {state.treasury.toLocaleString()} §
            </span>
          </div>

          {/* Felicità generale */}
          <div className="hud-item" title="Gradimento Sindaco" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Smile size={18} style={{ color: state.happiness > 70 ? '#4caf50' : (state.happiness > 45 ? '#ffb300' : '#ff1744') }} />
            <span style={{ fontWeight: 800, fontSize: '14px' }}>{state.happiness}%</span>
          </div>
        </div>

        {/* Mayor XP & Progression Progress Card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '240px', background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }} title={`Livello Mayor ${state.cityLevel} (${state.xp}/${state.xpNeeded} XP)`}>
          <Award size={18} style={{ color: '#ffeb3b' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 800, color: '#e0f2f1' }}>
              <span>LVL {state.cityLevel}</span>
              <span>{xpPercent}%</span>
            </div>
            {/* ProgressBar */}
            <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.3)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${xpPercent}%`, height: '100%', background: 'linear-gradient(90deg, #ffeb3b, #ffa726)', borderRadius: '3px', transition: 'width 0.4s ease' }} />
            </div>
          </div>
        </div>

        {/* Date and Time Panel */}
        <div className="hud-section" style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <div className="hud-item" title="Calendario di Swisslandia" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={18} style={{ color: '#ffa726' }} />
            <span style={{ fontSize: '13px', fontWeight: 700 }}>{state.date.day} {getMonthName(state.date.month)} {state.date.year}</span>
          </div>

          <div className="hud-item" title="Orologio Solare" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={18} style={{ color: '#b0bec5' }} />
            <span style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'monospace' }}>{formattedTime}</span>
          </div>
        </div>

        {/* Speed Controls and Management Button */}
        <div className="hud-section" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          
          {/* RCI indicators */}
          <div className="rci-container" style={{ display: 'flex', gap: '4px', background: 'rgba(0,0,0,0.25)', padding: '4px 6px', borderRadius: '6px', height: '24px', alignItems: 'flex-end' }} title="Richiesta RCI (Verde: Residenziale, Blu: Commerciale, Giallo: Industriale)">
            <div className="rci-meter" style={{ width: '5px', height: '100%', background: 'rgba(255,255,255,0.05)', position: 'relative' }}>
              <div className="rci-bar res" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: `${state.demand.residential}%`, background: '#4caf50' }}></div>
            </div>
            <div className="rci-meter" style={{ width: '5px', height: '100%', background: 'rgba(255,255,255,0.05)', position: 'relative' }}>
              <div className="rci-bar com" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: `${state.demand.commercial}%`, background: '#2196f3' }}></div>
            </div>
            <div className="rci-meter" style={{ width: '5px', height: '100%', background: 'rgba(255,255,255,0.05)', position: 'relative' }}>
              <div className="rci-bar ind" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: `${state.demand.industrial}%`, background: '#ffeb3b' }}></div>
            </div>
          </div>

          {/* Clock Speeds */}
          <div className="speed-controls" style={{ display: 'flex', gap: '2px', background: 'rgba(0,0,0,0.2)', padding: '2px', borderRadius: '6px' }}>
            <button className={`btn-speed ${state.simSpeed === 0 ? 'active' : ''}`} onClick={() => onSetSpeed(0)} title="Pausa" style={{ padding: '4px 6px', border: 'none', background: state.simSpeed === 0 ? '#26a69a' : 'transparent', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
              <Pause size={10} />
            </button>
            <button className={`btn-speed ${state.simSpeed === 1 ? 'active' : ''}`} onClick={() => onSetSpeed(1)} title="Velocità 1x" style={{ padding: '4px 6px', border: 'none', background: state.simSpeed === 1 ? '#26a69a' : 'transparent', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
              <Play size={10} />
            </button>
            <button className={`btn-speed ${state.simSpeed === 2 ? 'active' : ''}`} onClick={() => onSetSpeed(2)} title="Velocità 2x" style={{ padding: '4px 6px', border: 'none', background: state.simSpeed === 2 ? '#26a69a' : 'transparent', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
              <ChevronRight size={10} />
            </button>
            <button className={`btn-speed ${state.simSpeed === 5 ? 'active' : ''}`} onClick={() => onSetSpeed(5)} title="Velocità 5x" style={{ padding: '4px 6px', border: 'none', background: state.simSpeed === 5 ? '#26a69a' : 'transparent', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
              <ChevronsRight size={10} />
            </button>
          </div>

          <button 
            className="btn-panel-toggle" 
            onClick={() => onTogglePanel(activePanel === 'economy' ? 'none' : 'economy')}
            style={{ 
              padding: '6px 12px', 
              borderRadius: '8px', 
              fontSize: '12px', 
              fontWeight: 800, 
              cursor: 'pointer',
              color: 'white',
              background: activePanel === 'economy' ? '#26a69a' : 'rgba(255,255,255,0.08)',
              border: activePanel === 'economy' ? '1px solid #26a69a' : '1px solid rgba(255,255,255,0.15)',
              transition: 'all 0.2s ease',
              pointerEvents: 'auto'
            }}
          >
            Gestione
          </button>
        </div>

      </div>
    </div>
  );
};
